import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  useEventCallback,
} from '@mui/material';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { DecasedFormVM } from 'front-office/view-models/certificate/deceased-form';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import InputKeyboard from 'shell/components/shared/input-keyboard';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { AnprVM } from 'shell/view-models/search-anpr';
import { StatementsFormVM } from 'front-office/view-models/statements/statements-form';
import { transformSearchPersonDataset } from 'shell/utils/transform-search-person';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { transformerPersonGeneralityDeceasedList } from './transformer-anpr';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ItemVM } from 'shell/view-models/item';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import ListGender from 'shell/components/shared/gender';
import MunicipalityForeignList from 'shell/components/shared/municipality-foreign-list';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import { PersonVM } from 'shell/view-models/search-person';
import Table from 'shell/components/shared/table';
import { headCells } from '../generality/form';
import { noop } from 'shell/utils/noop';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';

interface DeceasedProps {
  decased?: DecasedFormVM;
  onFieldDecasedChange: (
    key: keyof DecasedFormVM,
    value: DecasedFormVM[keyof DecasedFormVM]
  ) => void;
  onFieldsDeceasedChange: (values: DecasedFormVM) => void;
}

const DeceasedComponent = ({
  onFieldDecasedChange,
  onFieldsDeceasedChange,
  decased,
}: DeceasedProps): React.ReactElement => {
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [visibilityTable, setVisibilityTable] = useState(false);

  const [paginationState, setPaginationState] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  const onChangeMunicipalityList = useEventCallback(
    (key: keyof DecasedFormVM, municipality: MunicipalityAnprVM | null) => {
      onFieldDecasedChange(key, municipality);
    }
  );

  const onChangeStateList = useEventCallback(
    (key: keyof DecasedFormVM, foreignState?: ForeignStateVM | null) => {
      onFieldDecasedChange(key, foreignState);
    }
  );

  const [anpr, getAnpr] = useHttpFetch<HttpResponse<ResponseAnprData>, AnprVM<StatementsFormVM>>(
    `${__PERSON_API_URL__}/persona/coniugeDeceduto`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonGeneralityDeceasedList,
    }
  );

  const getPersonDeceased = useHttpClient<HttpResponse<ResponseAnprData>, AnprVM<StatementsFormVM>>(
    `${__PERSON_API_URL__}/persona/coniugeDeceduto`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonGeneralityDeceasedList,
    }
  );

  // Reset state pagination
  const resetPagination = useEventCallback(() => {
    setPaginationState({
      page: 0,
      rowsPerPage: 10,
    });
  });

  const handleSearchDeceased = useEventCallback(() => {
    setVisibilityTable(false);

    if (!decased?.fiscalCode && !(decased?.name && decased?.surname)) {
      return;
    }

    const data = transformSearchPersonDataset({
      page: 0,
      rowsPerPage: 0,
      orders: {},
      subjectData: {
        fiscalCode: decased?.fiscalCode,
        birthday: decased?.dateBirth,
        name: decased?.name,
        surname: decased?.surname,
      },
      commonSearch: {
        isNationalResearch: true,
      },
    });

    (
      getAnpr({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<StatementsFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<StatementsFormVM>;

      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];

        onFieldsDeceasedChange({
          ...element.decased,
          // decasedFromSearch: true,
        });
        setVisibilityTable(true);
        return;
      }
      resetPagination();
      setVisibilityTable(true);
    });
  });

  const castedAnprData = anpr.data as AnprVM<StatementsFormVM>;

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? undefined : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  // Handle change page
  const handleChangePage = useEventCallback((page: number) => {
    setPaginationState((oldState) => ({ ...oldState, page }));
  });

  // Handle change rows per page
  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setPaginationState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  });

  const handleClickSelect = useEventCallback((person: StatementsFormVM) => {
    (
      getPersonDeceased({
        data: {
          criteriRicerca: {
            codiceFiscale: person.decased?.fiscalCode || undefined,
            idSchedaSoggettoANPR: person?.decased?.idSubjectAnpr,
          },
          datiRichiesta: {
            ricercaNazionale: '1',
          },
        },
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<StatementsFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<StatementsFormVM>;
      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];

        onFieldsDeceasedChange({
          ...element.decased,
          // decasedFromSearch: true,
        });

        setVisibilityTable(false);
      }
      resetPagination();
      setVisibility({});
    });
  });

  const dataPersons: Array<PersonVM> = useMemo(() => {
    return (
      castedAnprData?.persons?.map((person) => ({
        fiscalCode: person.decased?.fiscalCode || '',
        surname: person.decased?.surname || '',
        name: person.decased?.name || '',
        gender: person.decased?.gender || '',
        birthday: person.decased?.dateBirth?.toFormat('dd/MM/yyyy') || '',
        birthplace: person.decased?.birthplace,
        residence: person.decased?.residenceString,
        state: person.decased?.state,
        anpr: person.decased?.idSubjectAnpr,
        action: (
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => handleClickSelect(person)}
          >
            Seleziona
          </PmiButton>
        ),
      })) || []
    );
  }, [castedAnprData?.persons, handleClickSelect]);

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, anpr.data]);

  return (
    <Fragment>
      {visibilityTable || errorMessage.visibility ? (
        <>
          <SearchMessages
            type={MessageType.Error}
            title={errorMessage.title || ''}
            messages={errorMessage.messages || []}
            visibility={errorMessage.visibility || false}
            onChangeVisibility={onVisibility}
          />
          <SearchMessages
            type={MessageType.Warning}
            title={
              castedAnprData?.warnings?.length > 1
                ? `Ci sono ${castedAnprData?.warnings?.length} warning`
                : `C'è 1 warning`
            }
            messages={castedAnprData?.warnings || []}
            visibility={
              visibility.warning !== undefined
                ? visibility.warning
                : (castedAnprData?.warnings || [])?.length > 0
            }
            onChangeVisibility={onVisibility}
          />
        </>
      ) : undefined}
      <Grid container item spacing={2}>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <PmiTitleDivider>Deceduto</PmiTitleDivider>
        </Grid>
        <Grid
          item
          container
          xs={12}
          spacing={0}
          columnSpacing={{ xs: 0, md: 1 }}
          rowSpacing={{ xs: 1, md: 0 }}
        >
          <Grid item xs={12} md={4}>
            <PmiTextField
              label="Codice fiscale"
              aria-label="Codice fiscale"
              id="fiscal-code"
              fullWidth
              inputProps={{
                maxLength: '16',
              }}
              onChange={(event) => {
                onFieldDecasedChange('fiscalCode', event?.target.value);
              }}
              value={decased?.fiscalCode?.toUpperCase() || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label="Cognome"
              aria-label="Cognome"
              id="surname"
              fullWidth
              onChange={(event) => {
                onFieldDecasedChange('surname', event?.target.value);
              }}
              value={decased?.surname || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label="Nome"
              aria-label="Nome"
              id="name"
              fullWidth
              onChange={(event) => {
                onFieldDecasedChange('name', event?.target.value);
              }}
              value={decased?.name || ''}
            />
          </Grid>
        </Grid>
        <Grid
          item
          container
          xs={12}
          spacing={0}
          columnSpacing={{ xs: 0, md: 1 }}
          rowSpacing={{ xs: 1, md: 0 }}
        >
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data nascita"
              aria-label="Data nascita"
              value={decased?.dateBirth || null}
              onChange={(date) => {
                onFieldDecasedChange('dateBirth', date);
              }}
              renderInput={(params) => (
                <PmiTextField {...params} id="birthdate" fullWidth helperText="" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3} display="flex" alignSelf="center">
            <PmiButton
              variant="contained"
              color="secondary"
              fullWidth
              onClick={handleSearchDeceased}
            >
              Cerca deceduto
            </PmiButton>
          </Grid>
          <Grid item xs={12} md={3} display="flex" alignSelf="center">
            <PmiButton
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              onClick={() => {
                onFieldsDeceasedChange({
                  surname: '',
                  birthplace: '',
                  dateBirth: null,
                  dateDeath: null,
                  dateMarriage: null,
                  fiscalCode: '',
                  foreignStateBirth: null,
                  foreignStateOfDeath: null,
                  foreignStateOfMarriage: null,
                  gender: '',
                  idSubjectAnpr: '',
                  isBirthStateItalian: true,
                  isDeathStateItalian: true,
                  isMarriageStateItalian: true,
                  municipalityOfBirth: null,
                  municipalityOfDeath: null,
                  municipalityOfMarriage: null,
                  name: '',
                  withoutName: false,
                  withoutSurname: false,
                  residenceString: '',
                  state: '',
                });
              }}
            >
              Pulisci
            </PmiButton>
          </Grid>
        </Grid>
        {dataPersons.length > 1 && visibilityTable ? (
          <Grid item xs={12}>
            <Table
              ariaLabel="Lista Persone"
              page={paginationState.page}
              rowsPerPage={paginationState.rowsPerPage}
              body={dataPersons}
              count={dataPersons.length}
              head={headCells}
              onSort={noop}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onVisibilityPress={noop}
              orders={{}}
              isNotLayoutFixed
              noSort
              hideButtons
            />
          </Grid>
        ) : undefined}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12} md={9}>
          <InputKeyboard
            label="Cognome"
            id="surname"
            aria-label="Cognome"
            onChange={(value) => onFieldDecasedChange('surname', value)}
            value={decased?.surname || ''}
            // disabled={!decased?.decasedFromSearch}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurname"
                checked={decased?.withoutSurname || false}
                onChange={() => onFieldDecasedChange('withoutSurname', !decased?.withoutSurname)}
                inputProps={{ 'aria-label': 'Persona senza cognome' }}
                // disabled={!decased?.decasedFromSearch}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <InputKeyboard
            label="Nome"
            id="name"
            aria-label="Nome"
            onChange={(value) => onFieldDecasedChange('name', value)}
            value={decased?.name || ''}
            // disabled={!decased?.decasedFromSearch}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutName"
                checked={decased?.withoutName || false}
                onChange={() => onFieldDecasedChange('withoutName', !decased?.withoutName)}
                inputProps={{ 'aria-label': 'Persona senza nome' }}
                // disabled={!decased?.decasedFromSearch}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={12} md={5}>
            <ListGender
              onChange={(event) => onFieldDecasedChange('gender', event.target.value)}
              value={decased?.gender || ''}
              // disabled={!decased?.decasedFromSearch}
            />
          </Grid>
        </Grid>
        <Grid container item spacing={2} xs={12}>
          <Grid item xs={12} md={5}>
            <DatePicker
              label="Nato il"
              aria-label="Nato il"
              value={decased?.dateBirth || null}
              // disabled={!decased?.decasedFromSearch}
              onChange={(date) => {
                onFieldDecasedChange('dateBirth', date);
              }}
              renderInput={(params) => (
                <PmiTextField {...params} id="date-birth" fullWidth helperText="" />
              )}
            />
          </Grid>
          {!decased?.isBirthStateItalian ? (
            <Grid item xs={12} md={4}>
              <MunicipalityList
                value={decased?.municipalityOfBirth || null}
                onChange={(municipality) =>
                  onChangeMunicipalityList('municipalityOfBirth', municipality)
                }
                label="Comune nascita"
                id="municipalityOfBirth"
                fromDate={decased?.dateBirth}
                // disabled={!decased?.decasedFromSearch}
              />
            </Grid>
          ) : (
            <Fragment>
              <Grid item xs={12} md={4}>
                <ForeignStateList
                  value={decased?.foreignStateBirth || null}
                  onChange={(state) => onChangeStateList('foreignStateBirth', state)}
                  label="Stato estero di nascita"
                  id="foreignStateBirth"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MunicipalityForeignList
                  value={decased?.municipalityOfBirth || null}
                  onChange={(municipality) =>
                    onChangeMunicipalityList('municipalityOfBirth', municipality)
                  }
                  label="Comune nascita"
                  id="municipalityOfBirth"
                  foreignStateIstatCode={decased?.foreignStateBirth?.istatCode || undefined}
                  // disabled={!decased?.decasedFromSearch}
                />
              </Grid>
            </Fragment>
          )}
          <Grid item xs={12} md={3}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="Stato di nascita"
                id="birthState"
                value={decased?.isBirthStateItalian || 'false'}
                onChange={() =>
                  onFieldDecasedChange('isBirthStateItalian', !decased?.isBirthStateItalian)
                }
              >
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Italia"
                  // disabled={!decased?.decasedFromSearch}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Estero"
                  // disabled={!decased?.decasedFromSearch}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container item spacing={2} xs={12}>
          <Grid item xs={12} md={5}>
            <DatePicker
              label="Deceduto il"
              aria-label="Deceduto il"
              value={decased?.dateDeath || null}
              // disabled={!decased?.decasedFromSearch}
              onChange={(date) => {
                onFieldDecasedChange('dateDeath', date);
              }}
              renderInput={(params) => (
                <PmiTextField {...params} id="date-death" fullWidth helperText="" />
              )}
            />
          </Grid>
          {!decased?.isDeathStateItalian ? (
            <Grid item xs={12} md={4}>
              <MunicipalityList
                value={decased?.municipalityOfDeath || null}
                onChange={(municipality) =>
                  onChangeMunicipalityList('municipalityOfDeath', municipality)
                }
                label="Comune decesso"
                id="municipalityOfDeath"
                fromDate={decased?.dateDeath}
                // disabled={!decased?.decasedFromSearch}
              />
            </Grid>
          ) : (
            <Fragment>
              <Grid item xs={12} md={4}>
                <ForeignStateList
                  value={decased?.foreignStateOfDeath || null}
                  onChange={(state) => onChangeStateList('foreignStateOfDeath', state)}
                  label="Stato estero di morte"
                  id="foreignStateOfDeath"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MunicipalityForeignList
                  value={decased?.municipalityOfDeath || null}
                  onChange={(municipality) =>
                    onChangeMunicipalityList('municipalityOfDeath', municipality)
                  }
                  label="Comune decesso"
                  id="municipalityOfDeath"
                  // disabled={!decased?.decasedFromSearch}
                  foreignStateIstatCode={decased?.foreignStateOfDeath?.istatCode || undefined}
                />
              </Grid>
            </Fragment>
          )}
          <Grid item xs={12} md={3}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="Stato di decesso"
                id="deathState"
                value={decased?.isDeathStateItalian || 'false'}
                onChange={() =>
                  onFieldDecasedChange('isDeathStateItalian', !decased?.isDeathStateItalian)
                }
              >
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Italia"
                  // disabled={!decased?.decasedFromSearch}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Estero"
                  // disabled={!decased?.decasedFromSearch}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container item spacing={2} xs={12}>
          <Grid item xs={12} md={5}>
            <DatePicker
              label="Matrimonio il"
              aria-label="Matrimonio il"
              value={decased?.dateMarriage || null}
              onChange={(date) => {
                onFieldDecasedChange('dateMarriage', date);
              }}
              renderInput={(params) => (
                <PmiTextField {...params} id="date-marriage" fullWidth helperText="" />
              )}
              // disabled={!decased?.decasedFromSearch}
            />
          </Grid>
          {!decased?.isMarriageStateItalian ? (
            <Grid item xs={12} md={4}>
              <MunicipalityList
                value={decased?.municipalityOfMarriage || null}
                onChange={(municipality) =>
                  onChangeMunicipalityList('municipalityOfMarriage', municipality)
                }
                fromDate={decased?.dateMarriage}
                label="Comune matrimonio"
                id="municipalityOfMarriage"
                // disabled={!decased?.decasedFromSearch}
              />
            </Grid>
          ) : (
            <Fragment>
              <Grid item xs={12} md={4}>
                <ForeignStateList
                  value={decased?.foreignStateOfMarriage || null}
                  onChange={(state) => onChangeStateList('foreignStateOfMarriage', state)}
                  label="Stato estero di matrimonio"
                  id="foreignStateOfMarriage"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MunicipalityForeignList
                  value={decased?.municipalityOfMarriage || null}
                  onChange={(municipality) =>
                    onChangeMunicipalityList('municipalityOfMarriage', municipality)
                  }
                  label="Comune matrimonio"
                  id="municipalityOfMarriage"
                  // disabled={!decased?.decasedFromSearch}
                  foreignStateIstatCode={decased?.foreignStateOfMarriage?.istatCode || undefined}
                />
              </Grid>
            </Fragment>
          )}
          <Grid item xs={12} md={3}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="Stato del matrimonio"
                id="marriageState"
                value={decased?.isMarriageStateItalian || 'false'}
                onChange={() =>
                  onFieldDecasedChange('isMarriageStateItalian', !decased?.isMarriageStateItalian)
                }
              >
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Italia"
                  // disabled={!decased?.decasedFromSearch}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Estero"
                  // disabled={!decased?.decasedFromSearch}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    </Fragment>
  );
};

DeceasedComponent.displayName = 'Deceased component';

const Deceased = memo(DeceasedComponent);

export default Deceased;
